import { type FunctionComponent } from 'react'
import { type SelectChangeEvent } from '@mui/material'
import useIntl from '@/utils/intl'
import LocationSelect from '../LocationSelect'
import Modal from '@/pages/network-coverage/modal'
import ModalNetwork from '@/pages/network-coverage/modalNetwork'
import { type Building, type City, type District, type Quarter, type SelectItem } from '@/lib/network-coverage-v2/types'

interface HomeBroadbandTabProps {
  selectedCity: City | null
  selectedDistrict: District | null
  selectedQuarter: Quarter | null
  selectedBuilding: Building | null
  quarters: Quarter[]
  buildings: Building[]
  cityItems: SelectItem[]
  districtItems: SelectItem[]
  quarterItems: SelectItem[]
  buildingItems: SelectItem[]
  iconColor: string
  isHbbModalOpen: boolean
  isNetworkModalOpen: boolean
  onHbbModalOpenChange: (open: boolean) => void
  onNetworkModalOpenChange: (open: boolean) => void
  onCitySelect: (zipcode: string) => void
  onDistrictSelect: (zipcode: string) => void
  onQuarterSelect: (event: SelectChangeEvent<string>) => void
  onBuildingSelect: (event: SelectChangeEvent<string>) => void
}

const HomeBroadbandTab: FunctionComponent<HomeBroadbandTabProps> = ({
  selectedCity,
  selectedDistrict,
  selectedQuarter,
  selectedBuilding,
  quarters,
  buildings,
  cityItems,
  districtItems,
  quarterItems,
  buildingItems,
  iconColor,
  isHbbModalOpen,
  isNetworkModalOpen,
  onHbbModalOpenChange,
  onNetworkModalOpenChange,
  onCitySelect,
  onDistrictSelect,
  onQuarterSelect,
  onBuildingSelect
}) => {
  const { translate } = useIntl()

  const handleCitySelect = (event: SelectChangeEvent<string>): void => {
    onCitySelect(event.target.value)
  }

  const handleDistrictSelect = (event: SelectChangeEvent<string>): void => {
    onDistrictSelect(event.target.value)
  }

  const handleRequestClick = (): void => { onNetworkModalOpenChange(true) }
  const handleSendClick = (): void => { onHbbModalOpenChange(true) }
  const handleNetworkModalClose = (): void => { onNetworkModalOpenChange(false) }
  const handleHbbModalClose = (): void => { onHbbModalOpenChange(false) }

  return (
    <div>
      <div className='p-6 border-b border-secondaryMidStroke'>
        <LocationSelect
          label={translate('city')}
          value={selectedCity?.zipcode ?? ''}
          items={cityItems}
          onChange={handleCitySelect}
          iconColor={iconColor}
        />
        {selectedCity !== null && (
          <LocationSelect
            label={translate('district')}
            value={selectedDistrict?.zipcode ?? ''}
            items={districtItems}
            onChange={handleDistrictSelect}
            iconColor={iconColor}
          />
        )}
        {selectedDistrict !== null && (quarters?.length ?? 0) > 0 && (
          <LocationSelect
            label={translate('quarter')}
            value={selectedQuarter?.zipcode ?? ''}
            items={quarterItems}
            onChange={onQuarterSelect}
            iconColor={iconColor}
          />
        )}
        {selectedQuarter !== null && (buildings?.length ?? 0) > 0 && (
          <LocationSelect
            label={translate('apartment')}
            value={selectedBuilding?.id ?? ''}
            items={buildingItems}
            onChange={onBuildingSelect}
            iconColor={iconColor}
          />
        )}
        {selectedBuilding !== null && (
          <div className='flex justify-center items-center mt-2'>
            <button
              className='rounded-2xl bg-[#EF4444] text-white text-sm w-4/5 h-10 hover:bg-red-600 transition-colors duration-100 mt-2'
              onClick={handleRequestClick}
            >
              {translate('homebroadbandRequest')}
            </button>
          </div>
        )}
      </div>
      <div className='p-6' role='alert'>
        <div className='bg-[#FFFBEB] border-2 border-[#FDE289] rounded-2xl p-2 shadow-md flex flex-col items-center'>
          <div className='flex flex-row p-2'>
            <div className='py-1'>
              <svg
                className='fill-current h-6 w-6 text-yellow-500 mr-4'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
              >
                <path d='M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z' />
              </svg>
            </div>
            <div className='flex justify-center items-center'>
              <p className='text-sm font-bold text-justify'>
                {translate('homebroadbandWarning')}
              </p>
            </div>
          </div>
          <button
            className='rounded-2xl bg-[#EF4444] text-white w-48 h-6 hover:bg-red-600 transition-colors duration-100 mt-2'
            onClick={handleSendClick}
          >
            {translate('request.toSend')}
          </button>
          <ModalNetwork isVisible={isNetworkModalOpen} onClose={handleNetworkModalClose} />
          <Modal isVisible={isHbbModalOpen} onClose={handleHbbModalClose} />
        </div>
      </div>
    </div>
  )
}

export default HomeBroadbandTab
